import React from 'react';
import { Box, FormControlLabel, Switch, Checkbox, FormGroup, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomSettings } from 'subModule/src/services/invoice/types';
import { useUserLanguages } from '../hooks/useUserLanguages';
import { useDefaultLabels } from '../hooks/useDefaultLabels';

interface VehicleInfoSettingsPanelProps {
  settings: CustomSettings;
  onChange: (settings: Partial<CustomSettings>) => void;
}

const VehicleInfoSettingsPanel: React.FC<VehicleInfoSettingsPanelProps> = ({ settings, onChange }) => {
  const { t } = useTranslation();
  const { primaryLanguageName, secondaryLanguageName } = useUserLanguages();
  const { getVehicleLabel } = useDefaultLabels();

  const updateVehicleField = (field: string, value: boolean) => {
    const newVehicleFields = {
      ...settings.vehicleFields,
      [field]: value,
    };
    
    // Pre-fill labels when field is enabled
    if (value) {
      const defaultLabels = getVehicleLabel(field);
      const currentLabels = settings.vehicleLabels?.[field as keyof typeof settings.vehicleLabels];
      
      // Only pre-fill if labels are not set (undefined/null) or empty strings
      const needsPreFill = 
        !currentLabels || 
        (!currentLabels.primary && !currentLabels.secondary) ||
        (currentLabels.primary === '' && currentLabels.secondary === '');
      
      if (needsPreFill) {
        onChange({
          vehicleFields: newVehicleFields,
          vehicleLabels: {
            ...settings.vehicleLabels,
            [field]: {
              primary: defaultLabels.primary,
              secondary: defaultLabels.secondary,
            },
          },
        });
        return;
      }
    }
    
    onChange({
      vehicleFields: newVehicleFields,
    });
  };

  const updateVehicleLabel = (field: string, lang: 'primary' | 'secondary', value: string) => {
    onChange({
      vehicleLabels: {
        ...settings.vehicleLabels,
        [field]: {
          ...settings.vehicleLabels?.[field as keyof typeof settings.vehicleLabels],
          [lang]: value,
        },
      },
    });
  };

  const vehicleFieldOptions = [
    { key: 'make', label: 'make' },
    { key: 'model', label: 'model' },
    { key: 'year', label: 'year' },
    { key: 'plateNumber', label: 'plateNumber' },
    { key: 'vin', label: 'vin' },
    { key: 'color', label: 'color' },
    { key: 'fuelType', label: 'fuelType' },
    { key: 'mileage', label: 'mileage' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={settings.showVehicleInfo ?? true}
            onChange={(e) => onChange({ showVehicleInfo: e.target.checked })}
          />
        }
        label={t('showVehicleInfo')}
        sx={{ m: 0 }}
      />

      {settings.showVehicleInfo && (
        <Box>
          <label className="font-Saira font-[600] text-[13px] mb-2 block" style={{ color: '#333' }}>
            {t('vehicleFields')}
          </label>
          <FormGroup sx={{ mt: 1 }}>
            {vehicleFieldOptions.map(({ key, label }) => (
              <Box key={key} sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={settings.vehicleFields?.[key as keyof typeof settings.vehicleFields] ?? false}
                      onChange={(e) => updateVehicleField(key, e.target.checked)}
                    />
                  }
                  label={t(label)}
                  sx={{ m: 0 }}
                />
                {settings.vehicleFields?.[key as keyof typeof settings.vehicleFields] && (
                  <Box sx={{ ml: 4, mt: 1, display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder={(`${t(label)} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      title={(`${t(label)} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      value={settings.vehicleLabels?.[key as keyof typeof settings.vehicleLabels]?.primary ?? ''}
                      onChange={(e) => updateVehicleLabel(key, 'primary', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      placeholder={(`${t(label)} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      title={(`${t(label)} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
                      value={settings.vehicleLabels?.[key as keyof typeof settings.vehicleLabels]?.secondary ?? ''}
                      onChange={(e) => updateVehicleLabel(key, 'secondary', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </FormGroup>
        </Box>
      )}
    </Box>
  );
};

export default VehicleInfoSettingsPanel;
